import { Card, createTheme, withStyles } from '@material-ui/core';
import CardMedia from '@material-ui/core/CardMedia';
import { MuiThemeProvider } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/CheckCircle';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import React from 'react';
import { BLUE, GREEN, STORE_ITEMS_PROVISIONING_TYPES, STORE_ITEMS_STATUS_MAP, STORE_ITEMS_TYPES, STORE_ITEMS_TYPES_MAP, STORE_ITEMS_CATEGORIES_MAP } from '../../_config/consts';
import CloseCircleIconCustom from '../CustomIcons/CloseCircleIconCustom.jsx';
import RemoveIconCustom from '../CustomIcons/RemoveIconCustom.jsx';

const styles = theme => ({
  titleText: {
    margin: 0,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
    fontSize: 22,
    lineHeight: 1.3,
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  container: {
    borderRadius: 12,
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardBodyContainer: {
    padding: 20,
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  descriptionText: {
    marginTop: 8, 
    height: '95px',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    fontSize: 14,
    lineHeight: '1.6em',
    color: '#5a6c7d',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sectionContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 10,
  },
  logo: {
    position: 'absolute',
    height: 27,
    right: 10,
    top: 10,
  },
  media: {
    height: 170,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
  },
  activeContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  activeText: {
    color: GREEN,
    margin: 0,
    marginRight: 5,
    fontWeight: 600,
    fontSize: 15,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  statusBadge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 20,
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
  inactiveBadge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 20,
    backgroundColor: 'rgba(189, 189, 189, 0.15)',
  },
});

const theme = createTheme({
  palette: {
    primary: { 500: BLUE },
  },
});

const StoreItemCardStatusView = ({ isActive, classes, status }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 'fit-content' }}>
    {isActive ? (
      <div className={classes.statusBadge}>
        <Entity className={classes.activeText} key={isActive} entity="storeItemActive" />
        <CheckIcon style={{ fontSize: 16, color: GREEN, marginLeft: 4 }} />
      </div>
    ) : (
      <div className={classes.inactiveBadge}>
        <Entity className={classes.activeText} key={isActive} style={{ color: '#9e9e9e', fontSize: 15, fontWeight: 600 }} entity="inactive" />
        <RemoveIconCustom style={{ color: '#9e9e9e', width: 16, marginLeft: 4 }} />
      </div>
    )}
  </div>
);


const StoreItemTypeView = ({ itemType }) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'row', 
    alignItems: 'center',
    padding: '4px 10px', 
    borderRadius: 20,
    width: 'fit-content',
    backgroundColor: `${BLUE}15`,
  }}>
    <h4 style={{ fontSize: 12, margin: 0, color: BLUE, marginRight: 6, fontWeight: 600 }}>
      <Entity entity={STORE_ITEMS_TYPES_MAP[itemType].nameSingle} />
    </h4>
    <span style={{ color: BLUE, display: 'flex', alignItems: 'center' }}>{STORE_ITEMS_TYPES_MAP[itemType].icon}</span>
  </div>
);

const CategoryView = ({ category }) => {
  if (!category || !STORE_ITEMS_CATEGORIES_MAP[category]) return null;
  
  const categoryColor = '#607d8b';
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: 20,
      width: 'fit-content',
      backgroundColor: `${categoryColor}15`,
    }}>
      <h4 style={{ 
        fontSize: 12, 
        margin: 0, 
        color: categoryColor, 
        marginRight: 6, 
        fontWeight: 600 
      }}>
        <Entity entity={STORE_ITEMS_CATEGORIES_MAP[category].nameShort} />
      </h4>
      <span style={{ color: categoryColor, display: 'flex', alignItems: 'center' }}>
        {STORE_ITEMS_CATEGORIES_MAP[category].icon}
      </span>
    </div>
  );
};

const AddonStatusView = ({ status }) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 20,
    width: 'fit-content',
    backgroundColor: `${STORE_ITEMS_STATUS_MAP[status].color}15`,
  }}>
    <h4 style={{ 
      fontSize: 16, 
      margin: 0, 
      color: STORE_ITEMS_STATUS_MAP[status].color, 
      marginRight: 6, 
      fontWeight: 600 
    }}>
      <Entity entity={STORE_ITEMS_STATUS_MAP[status].name} />
    </h4>
    <span style={{ color: STORE_ITEMS_STATUS_MAP[status].color, display: 'flex', alignItems: 'center' }}>
      {STORE_ITEMS_STATUS_MAP[status].icon}
    </span>
  </div>
);


const StoreItemCard = ({ classes, item, onPress }) => {
  if (item.visible === false) return null;
  return (
    <MuiThemeProvider theme={theme}>
      <Card 
        className="card card-rounded" 
        style={{ 
          borderRadius: 12, 
          height: 420, 
          width: '31%', 
          marginRight: 15, 
          marginLeft: 10, 
          padding: 0, 
          marginBottom: 30, 
          position: 'relative',
          transition: 'all 0.3s ease',
          border: '1px solid #e8e8e8',
          overflow: 'hidden',
        }} 
        onClick={() => onPress()} 
        elevation={2}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div className={classes.container}>
          <CardMedia
            className={classes.media}
            image={item && item.previewImage ? item.previewImage : item.coverImage}
          />
          
          <div className={classes.cardBodyContainer}>
            <div className={classes.titleContainer}>
              <h3 className={classes.titleText}>
                {item.renderedTitle ? item.renderedTitle : <Entity entity={item.title} />}
              </h3>
            </div>
            
            {/* Status badges after title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 0 }}>
              {item.type !== STORE_ITEMS_TYPES.HARDWARE ? (
                <StoreItemCardStatusView
                  classes={classes}
                  isActive={item.isActive}
                  status={item.status}
                />
              ) : null}

              {item.status && (
                <AddonStatusView status={item.status} />
              )}
            </div>
            
            {item.preview ? (
              <div 
                style={{ 
                  height: '100px',
                  display: '-webkit-box',
                  fontSize: 14,
                  lineHeight: '1.6em',
                  color: '#5a6c7d',
                  marginTop: 15,
                  marginBottom: 10,
                }} 
                className="storeCardContentContainer" 
                dangerouslySetInnerHTML={{ __html: item.preview }} 
              />
            ) : (
              <h4 className={classes.descriptionText}>
                <Entity entity={item.shortDescription} />
              </h4>
            )}
            
            {/* Bottom badges row */}
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              <StoreItemTypeView itemType={item.type} />
              {item.category && (
                <CategoryView category={item.category} />
              )}
            </div>
          </div>
        </div>
      </Card>
    </MuiThemeProvider>
  );
};
export default withStyles(styles)(StoreItemCard);
